/**
 * Sliding-window stability detector for scale readings.
 *
 * Keeps the (t, weight) samples of the last `windowMs`. The load counts as stable when the samples cover
 * at least 80 % of the window (so a single fresh reading is never "stable") and all of them lie within
 * a band of 2 * toleranceG (i.e. +/- toleranceG around the middle). Window min/max are tracked with
 * monotonic deques, so push() is amortized O(1) regardless of the reading rate.
 */
import type { StabilityOptions } from './types';

/** Fraction of the window the samples must span before stability can be declared. */
const MIN_COVERAGE = 0.8;
/** Absorbs float noise when comparing the spread against the tolerance band. */
const EPSILON = 1e-9;
/** Compact the backing arrays once this many dead entries have accumulated at the front. */
const COMPACT_AFTER = 256;
/** Recompute the running sum from scratch this often so float error can't build up over hours. */
const RESUM_EVERY = 1024;

interface Sample {
  t: number;
  w: number;
}

/** FIFO of samples backed by an array with a moving head (amortized O(1) push/shift/pop). */
class SampleQueue {
  private items: Sample[] = [];
  private head = 0;

  get length(): number {
    return this.items.length - this.head;
  }
  first(): Sample | undefined {
    return this.items[this.head];
  }
  last(): Sample | undefined {
    return this.length > 0 ? this.items[this.items.length - 1] : undefined;
  }
  push(s: Sample): void {
    this.items.push(s);
  }
  shift(): Sample | undefined {
    if (this.length === 0) return undefined;
    const s = this.items[this.head++];
    if (this.head >= COMPACT_AFTER && this.head * 2 >= this.items.length) {
      this.items = this.items.slice(this.head);
      this.head = 0;
    }
    return s;
  }
  pop(): Sample | undefined {
    return this.length > 0 ? this.items.pop() : undefined;
  }
  clear(): void {
    this.items = [];
    this.head = 0;
  }
  forEach(fn: (s: Sample) => void): void {
    for (let i = this.head; i < this.items.length; i++) fn(this.items[i]!);
  }
}

export class StabilityDetector {
  private opts: StabilityOptions;
  private readonly window = new SampleQueue();
  /** Candidates for the window max: weights strictly decreasing from front to back. */
  private readonly maxQ = new SampleQueue();
  /** Candidates for the window min: weights strictly increasing from front to back. */
  private readonly minQ = new SampleQueue();
  private sum = 0;
  private pushesSinceResum = 0;
  private isStable = false;
  private mean: number | null = null;

  constructor(opts: StabilityOptions) {
    this.opts = sanitize(opts);
  }

  get stable(): boolean {
    return this.isStable;
  }

  /** Mean weight over the window while stable, otherwise null. */
  get stableWeight(): number | null {
    return this.isStable ? this.mean : null;
  }

  /** Max - min over the current window (0 when empty). Handy for "settling…" indicators. */
  get spread(): number {
    const max = this.maxQ.first();
    const min = this.minQ.first();
    return max && min ? max.w - min.w : 0;
  }

  get options(): StabilityOptions {
    return { ...this.opts };
  }

  push(t: number, weightG: number): void {
    if (!Number.isFinite(t) || !Number.isFinite(weightG)) return;
    const last = this.window.last();
    // A clock jump backwards means a new data source (e.g. reconnect / new driver): start over.
    if (last && t < last.t) this.clearSamples();

    const s: Sample = { t, w: weightG };
    this.window.push(s);
    this.sum += weightG;
    while (this.maxQ.length > 0 && this.maxQ.last()!.w <= weightG) this.maxQ.pop();
    this.maxQ.push(s);
    while (this.minQ.length > 0 && this.minQ.last()!.w >= weightG) this.minQ.pop();
    this.minQ.push(s);

    this.evictBefore(t - this.opts.windowMs);
    if (++this.pushesSinceResum >= RESUM_EVERY) this.resum();
    this.evaluate();
  }

  reset(): void {
    this.clearSamples();
    this.evaluate();
  }

  configure(opts: StabilityOptions): void {
    this.opts = sanitize(opts);
    const last = this.window.last();
    if (last) this.evictBefore(last.t - this.opts.windowMs);
    this.evaluate();
  }

  private clearSamples(): void {
    this.window.clear();
    this.maxQ.clear();
    this.minQ.clear();
    this.sum = 0;
    this.pushesSinceResum = 0;
  }

  private evictBefore(cutoff: number): void {
    for (let s = this.window.first(); s && s.t < cutoff; s = this.window.first()) {
      this.window.shift();
      this.sum -= s.w;
      if (this.maxQ.first() === s) this.maxQ.shift();
      if (this.minQ.first() === s) this.minQ.shift();
    }
  }

  private resum(): void {
    let total = 0;
    this.window.forEach((s) => (total += s.w));
    this.sum = total;
    this.pushesSinceResum = 0;
  }

  private evaluate(): void {
    const n = this.window.length;
    const first = this.window.first();
    const last = this.window.last();
    if (n < 2 || !first || !last) {
      this.isStable = false;
      this.mean = null;
      return;
    }
    const covered = last.t - first.t >= MIN_COVERAGE * this.opts.windowMs;
    this.isStable = covered && this.spread <= 2 * this.opts.toleranceG + EPSILON;
    this.mean = this.sum / n;
  }
}

function sanitize(o: StabilityOptions): StabilityOptions {
  const windowMs = Number.isFinite(o.windowMs) && o.windowMs > 0 ? o.windowMs : 800;
  const toleranceG = Number.isFinite(o.toleranceG) && o.toleranceG >= 0 ? o.toleranceG : 0.3;
  return { windowMs, toleranceG };
}
